import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as Mui from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { UserProfileDetails } from "./UserProfileDetails";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  designColors,
  designRadii,
  designShell,
  APP_BAR_OFFSET,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
} from "services/constants/designTokens";
import { adminUi } from "./adminListScreenUi";
import {
  GlobalDetailSectionCard as AdminDetailSectionCard,
  GlobalFieldLabel as AdminFieldLabel,
  GlobalNoticeBanner as AdminNoticeBanner,
  globalTextFieldSx as adminTextFieldSx,
  useExpandedContentLeft,
  GlobalBreadcrumb,
} from "components/DesignSystem/sharedUiPatterns";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import {
  detailAccentPalette,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

// Same accent palette TeamZone's Approve panels (Leave, Expense, etc.) draw
// from — cycled per field so this panel's icon rows read the same way
// instead of inventing its own colors.
const detailAccent = {
  fullName: detailAccentPalette.indigo,
  userCode: detailAccentPalette.pink,
  email: detailAccentPalette.blue,
  phone: detailAccentPalette.green,
  role: detailAccentPalette.orange,
  designation: detailAccentPalette.indigo,
  department: detailAccentPalette.pink,
  employmentType: detailAccentPalette.blue,
  departmentLead: detailAccentPalette.green,
  company: detailAccentPalette.orange,
  systemAccess: detailAccentPalette.purple,
};

const SectionTitle = ({ children }) => (
  <Mui.Typography sx={{ ...adminUi.panelSectionTitle, mt: 2, mb: 1 }}>
    {children}
  </Mui.Typography>
);

const ScopeNotice = () => (
  <AdminNoticeBanner
    sx={{ mb: 2.5 }}
    icon={
      <PersonAddAlt1Icon fontSize="small" />
    }
  >
    Update this user's system login details, company assignment, and access
    level.
  </AdminNoticeBanner>
);

export const UserDetailPanel = ({
  open,
  onClose,
  user,
  onMoreActions,
  onSoleMenuAction,
  onDeactivate,
  onEdit,
  onSaveEdit,
  checkAccess,
  // Users has no real approval workflow, but the permission flag still
  // exists on the submodule -- see Administration/index.js for the full
  // edit-vs-approve split this gates (Deactivate/Activate here, vs. the
  // Edit action on checkAccess; the More Actions dropdown's own items are
  // gated separately in updateMenuItem).
  hasApproverAccess,
  // Whether the More Actions dropdown has anything in it at all for this
  // user, and -- when it has exactly one thing -- which single dropMenu
  // label that is (see getUserDropdownOptions in Administration/index.js).
  // A one-item menu skips straight to a direct button for that action
  // instead of forcing an extra click through a dropdown with nothing else
  // in it.
  hasMenuOptions = true,
  soleMenuAction = null,
  startInEditMode = false,
  profileMode = false,
  expanded = false,
  onToggleExpand,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const contentLeft = useExpandedContentLeft(expanded);
  const { domain } = useSelector((state) => state.tokenReducer);
  const { result: companyDetails } = useFetch(
    domain ? `${domain}get-active-company/` : null,
  );

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      user_type: user?.type || "User",
      name: user?.user_name || "",
      phoneno: user?.phonenumber || "",
      email: user?.email || "",
      company: user?.company_name || "",
      company_id: user?.company_id || null,
    },
  });

  useEffect(() => {
    if (!user) return;
    reset({
      user_type: user?.type || "User",
      name: user?.user_name || "",
      phoneno: user?.phonenumber || "",
      email: user?.email || "",
      company: user?.company_name || "",
      company_id: user?.company_id || null,
    });
    setEditMode(startInEditMode);
  }, [reset, startInEditMode, user]);

  useEffect(() => {
    if (!expanded) return;
    if (typeof document === "undefined") return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [expanded]);

  const companyOptions = useMemo(
    () =>
      (companyDetails || []).map((obj) => ({
        ...obj,
        label: obj.companyname,
      })),
    [companyDetails],
  );

  if (!user) return null;

  const isActive = user?.status === "Active";
  const headerStatus = isActive ? "Active" : "Inactive";
  const requestHeaderUserDetails = {
    image_url: user?.image_url,
    employee_id: user?.employee_id || user?.user_code || "-",
    user_name: user?.user_name || "N/A",
    department_name: user?.department || "-",
    designation: user?.designation || user?.type || "-",
  };

  const handleCompanyName = (event, data) => {
    if (data) {
      setValue("company", data?.label);
      setValue("company_id", data?.id);
      return;
    }
    setValue("company", "");
    setValue("company_id", null);
  };

  const handleUserType = (event) => {
    setValue("user_type", event.target.value);
  };

  const handleKeyPress = (event) => {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  };

  const checkSpecialChar = (event) => {
    if (!/^[a-zA-Z0-9 ]*$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleCancelEdit = () => {
    reset({
      user_type: user?.type || "User",
      name: user?.user_name || "",
      phoneno: user?.phonenumber || "",
      email: user?.email || "",
      company: user?.company_name || "",
      company_id: user?.company_id || null,
    });
    setEditMode(false);
  };

  const handleCloseAction = () => {
    if (editMode) {
      handleCancelEdit();
      return;
    }
    onClose?.();
  };

  const submitEdit = async (data) => {
    if (!onSaveEdit) return;
    setSaving(true);
    const ok = await onSaveEdit(user, data);
    setSaving(false);
    if (ok) {
      setEditMode(false);
    }
  };

  const nameField = (
    <Mui.Box>
      <AdminFieldLabel helperText="Enter the full name of the user. This will be used to identify the individual in the system.">
        User Name
      </AdminFieldLabel>
      <Mui.TextField
        {...register("name", {
          required: "Name is required",
          maxLength: {
            value: 30,
            message: "Maximum length is 30 characters",
          },
          minLength: {
            value: 3,
            message: "Minimum length is 3 characters",
          },
          validate: {
            noDigitsOrSpecial: (fieldValue) => {
              if (!/[A-Za-z]/.test(fieldValue)) {
                return "Name must contain at least one alphabet character.";
              }
              if (/^\d+$/.test(fieldValue)) {
                return "Name should not contain only digits.";
              }
              if (!/^[A-Za-z0-9\s&-]+$/.test(fieldValue)) {
                return "Name should only contain alphabets, spaces, -,numbers and & symbol.";
              }
              return true;
            },
          },
        })}
        size="small"
        fullWidth
        error={Boolean(errors.name)}
        helperText={errors.name ? errors.name.message : null}
        placeholder="Enter the name"
        onKeyPress={checkSpecialChar}
        onBlur={() => trigger("name")}
        sx={adminTextFieldSx}
      />
    </Mui.Box>
  );

  const phoneField = (
    <Mui.Box>
      <AdminFieldLabel helperText="Input the user's active mobile number. This may be used for notifications, login authentication, or user verification.">
        Phone Number
      </AdminFieldLabel>
      <Mui.TextField
        {...register("phoneno", {
          required: "Phone number is required",
          minLength: {
            value: 10,
            message: "Minimum length is 10 digits",
          },
          maxLength: {
            value: 15,
            message: "Maximum length is 15 digits",
          },
        })}
        size="small"
        fullWidth
        onKeyPress={handleKeyPress}
        placeholder="Enter the phone number"
        error={Boolean(errors.phoneno)}
        helperText={errors.phoneno ? errors.phoneno.message : null}
        sx={adminTextFieldSx}
      />
    </Mui.Box>
  );

  const companyField = (
    <Mui.Box>
      <AdminFieldLabel helperText="Select the company the user is associated with from the dropdown list. This links the user to relevant departments, permissions, and data access.">
        Company Name
      </AdminFieldLabel>
      <CustomAutoComplete
        dataList={companyOptions}
        selectedValue={watch("company")}
        rules={{
          ...register("company", {
            required: "Company name is required",
          }),
        }}
        updateDataFunction={handleCompanyName}
        placeHolder="Select the company name"
        errorMessage={errors?.company?.message ? errors?.company?.message : ""}
        textBoxError={Boolean(errors.company)}
      />
    </Mui.Box>
  );

  const emailField = (
    <Mui.Box>
      <AdminFieldLabel helperText="Provide a valid email address for the user. The email is essential for communication, system alerts, and possibly login credentials.">
        Email
      </AdminFieldLabel>
      <Mui.TextField
        {...register("email", {
          required: false,
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid e-mail address",
          },
        })}
        error={Boolean(errors.email)}
        helperText={errors.email ? errors.email.message : null}
        placeholder="Enter the email"
        size="small"
        fullWidth
        sx={adminTextFieldSx}
      />
    </Mui.Box>
  );

  const userTypeRadio = (
    <Mui.Radio
      sx={{
        color: "#9CA3AF",
        "&.Mui-checked": {
          color: designColors.tealDark,
        },
        "&:hover": {
          backgroundColor: "rgba(15, 118, 110, 0.08)",
        },
      }}
    />
  );

  const userTypeField = (
    <Mui.Box>
      <AdminFieldLabel helperText="Choose whether this login should have standard user access or Super Admin access.">
        User Type
      </AdminFieldLabel>
      <Mui.RadioGroup
        row
        onChange={handleUserType}
        aria-labelledby="panel-edit-user-type-label"
        value={watch("user_type")}
        name="radio-buttons-group"
        sx={{
          gap: 2,
          flexWrap: "wrap",
          "& .MuiFormControlLabel-label": {
            fontSize: 14,
            color: designColors.slate800,
          },
        }}
      >
        <Mui.FormControlLabel label="User" value="User" control={userTypeRadio} />
        <Mui.FormControlLabel
          label="Super Admin"
          value="Super Admin"
          control={userTypeRadio}
        />
      </Mui.RadioGroup>
    </Mui.Box>
  );

  const viewContent = (
    <Mui.Stack spacing={2}>
      <AdminDetailSectionCard title="Identity">
            <Mui.Stack>
              <RequestDetailField
                icon={PersonOutlineOutlinedIcon}
                label="Full Name"
                value={user?.user_name}
                accent={detailAccent.fullName}
              />
              <RequestDetailField
                icon={BadgeOutlinedIcon}
                label="User Code"
                value={user?.user_code}
                accent={detailAccent.userCode}
              />
              <RequestDetailField
                icon={EmailOutlinedIcon}
                label="Email"
                value={user?.email}
                accent={detailAccent.email}
              />
              <RequestDetailField
                icon={CallOutlinedIcon}
                label="Phone"
                value={user?.phonenumber}
                accent={detailAccent.phone}
                isLast
              />
            </Mui.Stack>
          </AdminDetailSectionCard>

          <AdminDetailSectionCard title="Work Information">
            <Mui.Stack>
              <RequestDetailField
                icon={SecurityOutlinedIcon}
                label="Role"
                value={user?.type}
                accent={detailAccent.role}
              />
              <RequestDetailField
                icon={WorkOutlineOutlinedIcon}
                label="Designation"
                value={user?.designation}
                accent={detailAccent.designation}
              />
              <RequestDetailField
                icon={ApartmentOutlinedIcon}
                label="Department"
                value={user?.department}
                accent={detailAccent.department}
              />
              <RequestDetailField
                icon={AccessTimeOutlinedIcon}
                label="Employment Type"
                value={user?.employment_type}
                accent={detailAccent.employmentType}
              />
              <RequestDetailField
                icon={SupervisorAccountOutlinedIcon}
                label="Department Lead"
                value={user?.department_lead_name}
                accent={detailAccent.departmentLead}
              />
              <RequestDetailField
                icon={BusinessOutlinedIcon}
                label="Company"
                value={user?.company_name}
                accent={detailAccent.company}
              />
              <RequestDetailField
                icon={VpnKeyOutlinedIcon}
                label="System Access"
                value={user?.system_access === "Yes" ? "Enabled" : "Disabled"}
                accent={detailAccent.systemAccess}
                isLast
              />
            </Mui.Stack>
      </AdminDetailSectionCard>
    </Mui.Stack>
  );

  // Aligned to the end (flex-end) rather than stretched full-width — matches
  // CompanyDetailPanel's edit footer, which this panel mirrors. Every
  // action here (Edit, Update, More Actions, Deactivate/Activate) used to
  // show for anyone regardless of access, with at most a disabled state or
  // a toast on click -- hidden now instead, same as Companies.
  const footerContent = editMode ? (
    <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
      <SecondaryButton onClick={handleCancelEdit}>Cancel</SecondaryButton>
      {/* Unreachable via the UI once the header Edit button below is
          hidden for a non-editor, but guarded directly too rather than
          relying only on that. */}
      {checkAccess ? (
        <PrimaryButton
          onClick={handleSubmit(submitEdit)}
          loading={saving}
          disabled={saving}
        >
          Update
        </PrimaryButton>
      ) : null}
    </Mui.Stack>
  ) : !(hasMenuOptions || hasApproverAccess) ? (
    // Nothing behind either button -- see getUserDropdownOptions in
    // Administration/index.js for exactly when hasMenuOptions is false
    // (e.g. an Invited/Expired user with no editor access). Hidden
    // entirely, same as Companies.
    null
  ) : (
    <Mui.Stack spacing={1.5}>
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            hasMenuOptions && hasApproverAccess
              ? "repeat(2, minmax(0, 1fr))"
              : "1fr",
          gap: 1.5,
        }}
      >
        {!hasMenuOptions ? null : soleMenuAction ? (
          // Exactly one thing behind the menu (typically just "View
          // Profile") -- skip the dropdown and act on it directly instead
          // of forcing an extra click through a menu with nothing else in
          // it.
          <Mui.Button
            variant="outlined"
            onClick={() => onSoleMenuAction?.(user)}
            sx={{
              flex: 1,
              ...designShell.panelNeutralButton,
              "&:hover": {
                ...designShell.panelNeutralButton["&:hover"],
              },
            }}
          >
            {soleMenuAction}
          </Mui.Button>
        ) : (
          <Mui.Button
            variant="outlined"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => onMoreActions?.(user, e)}
            sx={{
              flex: 1,
              ...designShell.panelNeutralButton,
              "&:hover": {
                ...designShell.panelNeutralButton["&:hover"],
              },
            }}
          >
            More Actions
          </Mui.Button>
        )}
        {hasApproverAccess ? (
        <Mui.Button
          variant="outlined"
          startIcon={isActive ? <PersonOffIcon /> : <PersonAddAlt1Icon />}
          onClick={() => onDeactivate?.(user)}
          sx={{
            flex: 1,
            ...designShell.panelDangerButton,
            color: isActive ? "#B42318" : designColors.tealDark,
            borderColor: isActive ? "#F5C2C7" : "#B7E4DB",
            backgroundColor: isActive ? "#FEF3F2" : designColors.tealTint,
            "&:hover": {
              borderColor: isActive ? "#E59CA5" : designColors.tealDark,
              backgroundColor: isActive ? "#FEE4E2" : "#DDF4EE",
            },
          }}
        >
          {isActive ? "Deactivate User" : "Activate User"}
        </Mui.Button>
        ) : null}
      </Mui.Box>
    </Mui.Stack>
  );

  const panelContent = (
    <Mui.Box
      sx={{
        width: expanded ? "100%" : PANEL_DRAWER_WIDTH,
        maxWidth: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxSizing: "border-box",
        backgroundColor: designColors.white,
        borderRadius: expanded ? designRadii.card : 0,
      }}
      role="presentation"
    >
      <Mui.Box sx={{ p: 3, pb: 0 }}>
        {expanded ? (
          <GlobalBreadcrumb
            trail={[
              "Administration",
              { label: "Users", onClick: handleCloseAction },
              user?.user_name || "User Details",
            ]}
          />
        ) : null}
        <Mui.Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            pb: editMode ? 2.25 : 1.5,
            borderBottom: editMode ? `1px solid ${designColors.slate200}` : "none",
          }}
        >
          <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
            {editMode ? (
              <>
                <Mui.Typography sx={{ ...adminUi.panelTitle, fontSize: 17 }}>
                  {user?.user_name || "N/A"}
                </Mui.Typography>
                <Mui.Typography sx={{ ...adminUi.panelMutedText, mt: 0.4 }}>
                  Editing this user's login and access details.
                </Mui.Typography>
              </>
            ) : (
              <>
                <Mui.Typography sx={{ ...adminUi.panelTitle, mb: 0.35 }}>
                  {profileMode ? "User Profile" : "User Details"}
                </Mui.Typography>
                <Mui.Typography sx={adminUi.panelMutedText}>
                  {profileMode
                    ? "Review this user's personal and employment information."
                    : "Review user information, access details, and account status."}
                </Mui.Typography>
              </>
            )}
          </Mui.Box>
          <Mui.Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ flexShrink: 0 }}
          >
            {/* Hidden rather than shown-and-blocked for a non-editor --
                same treatment as CompanyDetailPanel. There was no check at
                all here before. */}
            {!profileMode && !editMode && checkAccess ? (
              expanded ? (
                <PanelHeaderIconButton
                  expanded
                  title="Edit"
                  onClick={() => {
                    onEdit?.(user);
                    setEditMode(true);
                  }}
                >
                  <EditIcon />
                </PanelHeaderIconButton>
              ) : (
                <Mui.Tooltip title="Edit user">
                  <Mui.IconButton
                    aria-label="Edit user"
                    size="small"
                    onClick={() => {
                      onEdit?.(user);
                      setEditMode(true);
                    }}
                    sx={{
                      ...designShell.panelSoftIconButton,
                      color: designColors.slate500,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Mui.IconButton>
                </Mui.Tooltip>
              )
            ) : null}
            {expanded ? (
              <PanelHeaderIconButton
                expanded
                title="Collapse"
                onClick={onToggleExpand}
              >
                <CloseFullscreenIcon />
              </PanelHeaderIconButton>
            ) : (
              <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                <Mui.IconButton
                  onClick={onToggleExpand}
                  size="small"
                  sx={{
                    ...designShell.panelSoftIconButton,
                  }}
                >
                  <OpenFullViewIcon fontSize="small" />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}
            {expanded ? (
              <PanelHeaderIconButton
                expanded
                title="Close"
                onClick={handleCloseAction}
              >
                <CloseIcon />
              </PanelHeaderIconButton>
            ) : (
              <Mui.IconButton
                onClick={handleCloseAction}
                size="small"
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <CloseIcon />
              </Mui.IconButton>
            )}
          </Mui.Stack>
        </Mui.Stack>

        {!editMode ? (
          <Mui.Box sx={{ pb: 1.5 }}>
            <RequestDetailHeader
              userDetails={requestHeaderUserDetails}
              status={headerStatus}
              colorCode={user?.color_code}
            />
          </Mui.Box>
        ) : null}

        <Mui.Box sx={{ borderBottom: `1px solid ${designColors.slate200}` }} />
      </Mui.Box>

      <Mui.Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 3,
          pt: 2,
          pb: 2,
        }}
      >
        {profileMode ? (
          <UserProfileDetails userId={user.id} />
        ) : editMode ? (
          <Mui.Box component="form" onSubmit={handleSubmit(submitEdit)}>
            <ScopeNotice />
            <Mui.Grid container spacing={2.5}>
              <Mui.Grid item xs={12}>
                {nameField}
              </Mui.Grid>
              <Mui.Grid item xs={12}>
                {phoneField}
              </Mui.Grid>
              <Mui.Grid item xs={12}>
                {companyField}
              </Mui.Grid>
              <Mui.Grid item xs={12}>
                {emailField}
              </Mui.Grid>
              <Mui.Grid item xs={12}>
                {userTypeField}
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Box>
        ) : (
          viewContent
        )}
      </Mui.Box>

      {!profileMode && footerContent ? (
        <Mui.Box
          sx={{
            px: 3,
            py: 1.25,
            borderTop: "1px solid #E2E8F0",
            backgroundColor: designColors.white,
          }}
        >
          {footerContent}
        </Mui.Box>
      ) : null}
    </Mui.Box>
  );

  if (expanded) {
    return createPortal(
      <Mui.Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Mui.Box
          sx={{
            height: "100%",
            minHeight: "100%",
            width: "100%",
          }}
        >
          {panelContent}
        </Mui.Box>
      </Mui.Box>,
      document.body,
    );
  }

  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={handleCloseAction}
      sx={panelDrawerSx()}
    >
      {panelContent}
    </Mui.Drawer>
  );
};
